import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { Link, SidebarItem } from '@backstage/core-components';
import { Popover, Typography, makeStyles } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import AppsIcon from '@material-ui/icons/Apps';
import { kindByTemplateName } from '../platform/kinds';
import {
  orderable,
  shouldShowTenant,
  useTemplateFamilies,
  useTemplates,
} from '../platform/templates';

// Hover opens the menu only after a full second: short enough to feel like a
// shortcut, long enough that passing the cursor over Provision on the way to
// Catalog does not fire it. Clicking skips the wait. Leaving the rail entry or
// the paper closes it after a short grace, so the gap between them is
// crossable.
const OPEN_DELAY_MS = 1000;
const CLOSE_DELAY_MS = 260;

// The theme keeps its mono stack private, so the brand token is restated here.
const MONO =
  '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const useStyles = makeStyles(theme => ({
  anchor: {
    display: 'block',
  },
  popover: {
    // Lets the pointer travel from the sidebar item to the paper without the
    // popover swallowing the move and closing itself.
    pointerEvents: 'none',
  },
  paper: {
    pointerEvents: 'auto',
    width: 520,
    maxWidth: '90vw',
    maxHeight: '74vh',
    overflowY: 'auto',
    padding: theme.spacing(1, 0),
  },
  familyLabel: {
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    fontSize: 11,
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5, 2, 0.5),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.75, 2),
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  allRow: {
    paddingBottom: theme.spacing(1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  allIcon: {
    // Matched to the width of a kind monogram so both column up.
    width: 28,
    fontSize: 24,
    color: theme.palette.primary.main,
    flex: '0 0 auto',
  },
  rowTitle: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  // The title and the tenant chip travel together and share the row's free
  // space, so the chip stays against the title instead of drifting out to the
  // right where the template name already is.
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 auto',
    minWidth: 0,
  },
  templateTitle: {
    flex: '0 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  allTitle: {
    fontWeight: 600,
  },
  meta: {
    marginLeft: theme.spacing(2),
    flex: '0 0 auto',
    color: theme.palette.text.secondary,
  },
  // Sits against the title rather than out on the right with the template
  // name: it is what tells two otherwise identical rows apart, so it has to be
  // read as part of the title and not as a second column of metadata.
  tenant: {
    marginLeft: theme.spacing(1),
    flex: '0 0 auto',
    padding: theme.spacing(0, 0.75),
    borderRadius: 9,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: 10,
    letterSpacing: '0.3px',
    lineHeight: '17px',
    whiteSpace: 'nowrap',
  },
  id: {
    fontFamily: MONO,
  },
  empty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 2),
  },
}));

/**
 * "Provision" in the sidebar, with a hover flyout of what can be ordered.
 *
 * The families come from the templates' own tags rather than a list kept here:
 * the IDP ingests every plugin's template into the catalog automatically, so a
 * hardcoded menu would omit each newly shipped family until somebody noticed.
 * Day-2 templates are filtered out - they act on a stack that already exists
 * and belong on that stack's page, not under "make me a new thing".
 */
export const ProvisionMenu = () => {
  const classes = useStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const [open, setOpen] = useState(false);
  const { loading, value: templates } = useTemplates();
  const families = useTemplateFamilies(templates);
  const orderableTemplates = useMemo(() => orderable(templates), [templates]);
  const orderableCount = orderableTemplates.length;
  const showTenant = useMemo(
    () => shouldShowTenant(orderableTemplates),
    [orderableTemplates],
  );

  const cancelTimer = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const scheduleOpen = useCallback(() => {
    cancelTimer();
    timer.current = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  }, [cancelTimer]);

  const scheduleClose = useCallback(() => {
    cancelTimer();
    timer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [cancelTimer]);

  const close = useCallback(() => {
    cancelTimer();
    setOpen(false);
  }, [cancelTimer]);

  const toggle = useCallback(
    (event: ReactMouseEvent) => {
      // The entry keeps its href, so it still highlights on /create and opens
      // in a new tab, but a plain click belongs to the menu rather than to the
      // catalogue page - which is itself the first row of that menu.
      event.preventDefault();
      cancelTimer();
      setOpen(value => !value);
    },
    [cancelTimer],
  );

  useEffect(() => cancelTimer, [cancelTimer]);

  useEffect(() => {
    if (!open) return undefined;
    // A dismissing click must not also activate what is underneath it. A
    // backdrop would swallow it, but it would also sit between the pointer and
    // the rail and kill the hover that opened the menu - so the click is caught
    // on the way down instead, before it reaches its target.
    const swallow = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest(`.${classes.paper}`) ||
        anchorRef.current?.contains(target)
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    document.addEventListener('click', swallow, true);
    return () => document.removeEventListener('click', swallow, true);
  }, [open, close, classes.paper]);

  return (
    <>
      <div
        ref={anchorRef}
        className={classes.anchor}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
      >
        <SidebarItem
          icon={AddCircleOutlineIcon}
          to="/create"
          text="Provision"
          onClick={toggle}
        />
      </div>
      <Popover
        open={open && !loading}
        anchorEl={anchorRef.current}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        className={classes.popover}
        classes={{ paper: classes.paper }}
        disableRestoreFocus
        PaperProps={{
          onMouseEnter: cancelTimer,
          onMouseLeave: scheduleClose,
        }}
      >
        <Link
          to="/create"
          className={`${classes.row} ${classes.allRow}`}
          onClick={close}
        >
          <AppsIcon className={classes.allIcon} />
          <Typography
            variant="body2"
            className={`${classes.rowTitle} ${classes.allTitle}`}
          >
            All templates
          </Typography>
          <Typography variant="caption" className={classes.meta}>
            {orderableCount} templates
          </Typography>
        </Link>
        {families.length === 0 ? (
          <Typography variant="body2" className={classes.empty}>
            No templates are available to you yet.
          </Typography>
        ) : (
          families.map(family => (
            <div key={family.id}>
              <Typography className={classes.familyLabel}>
                {family.label}
              </Typography>
              {family.templates.map(template => {
                // Per template, not per family: four VM templates under one
                // family would otherwise be four identical squares, and the
                // glyph is the fastest way to tell Ubuntu from Azure.
                const { Icon } = kindByTemplateName(
                  template.name,
                  template.familyId,
                );
                return (
                  <Link
                    key={template.key}
                    to={template.path}
                    className={classes.row}
                    onClick={close}
                  >
                    <Icon />
                    <span className={classes.titleGroup}>
                      {/* component="span": body2 renders a <p>, which is not
                          allowed inside the phrasing content of a <span>. */}
                      <Typography
                        variant="body2"
                        component="span"
                        className={classes.templateTitle}
                      >
                        {template.title}
                      </Typography>
                      {/* Only when there is something to tell apart: with a
                          single tenant the chip is the same word on every row
                          and the row it labels is already unambiguous. */}
                      {showTenant && template.tenant && (
                        <Typography
                          variant="caption"
                          component="span"
                          className={classes.tenant}
                        >
                          {/* The chip is a bare span, a role that carries no
                              accessible name, so an aria-label on it is
                              dropped and a screen reader would read out the
                              tenant with nothing saying what it is. Said in
                              text instead, hidden from the eye and not from
                              the reader. */}
                          <Typography variant="srOnly">Tenant </Typography>
                          {template.tenant}
                        </Typography>
                      )}
                    </span>
                    <Typography
                      variant="caption"
                      className={`${classes.meta} ${classes.id}`}
                    >
                      {template.name}
                    </Typography>
                  </Link>
                );
              })}
            </div>
          ))
        )}
      </Popover>
    </>
  );
};
